import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { fetchGameAnalysis } from '../api/gameAnalysis';
import { fetchGameRecap } from '../api/gameRecap';
import RecapHero from './recap/RecapHero';
import RecapDecisions from './recap/RecapDecisions';
import RecapTopPerformers from './recap/RecapTopPerformers';
import RecapTimeline from './recap/RecapTimeline';
import RecapInningBreakdown from './recap/RecapInningBreakdown';
import RecapVideos from './recap/RecapVideos';

const { FiX, FiUsers, FiActivity, FiList, FiGrid, FiVideo } = FiIcons;

const BASE_TABS = [
  { id: 'summary', label: 'Summary', icon: FiActivity },
  { id: 'performers', label: 'Performers', icon: FiUsers },
  { id: 'timeline', label: 'Timeline', icon: FiList },
  { id: 'innings', label: 'Innings', icon: FiGrid },
];

const hasVideos = (content) => {
  const items = content?.highlights?.highlights?.items || [];
  return items.some(i => i.type === 'video' || i.playbacks?.length > 0);
};

const GameRecap = ({ game, onClose }) => {
  const [feed, setFeed] = useState(null);
  const [content, setContent] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    if (!game?.gamePk) return;
    setLoading(true);
    Promise.all([
      fetchGameRecap(game.gamePk),
      fetchGameAnalysis(game.gamePk),
    ])
      .then(([recapData, analysisData]) => {
        setFeed(recapData.feed);
        setContent(recapData.content);
        setAnalysis(analysisData);
      })
      .catch(() => setError('Could not load game recap.'))
      .finally(() => setLoading(false));
  }, [game?.gamePk]);

  const tabs = [
    ...BASE_TABS,
    ...(hasVideos(content) ? [{ id: 'videos', label: 'Videos', icon: FiVideo }] : []),
  ];

  const awayAbbr = game?.teams?.away?.team?.abbreviation || 'AWY';
  const homeAbbr = game?.teams?.home?.team?.abbreviation || 'HME';
  const innings = analysis?.linescoreData?.innings || [];
  const allPlays = analysis?.playByPlay?.allPlays || [];

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-2xl bg-[#1e3a29] border-4 border-[#d8ceb3] shadow-2xl z-10 max-h-[92vh] flex flex-col"
          initial={{ scale: 0.9, y: 30 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 30 }}
          transition={{ type: 'spring', stiffness: 280, damping: 24 }}
        >
          {/* Header */}
          <div className="bg-[#386646] border-b-2 border-[#658d71] p-3 sm:p-4 flex items-center justify-between shrink-0">
            <div>
              <p className="text-[9px] tracking-widest uppercase text-[#a9c9b1] font-bold">Game Recap</p>
              <p className="text-base sm:text-lg font-black text-[#d8ceb3] uppercase tracking-widest">
                {awayAbbr} vs {homeAbbr}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 border border-[#658d71] text-[#d8ceb3] hover:bg-[#658d71] transition-colors"
            >
              <SafeIcon icon={FiX} size={18} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b-2 border-[#658d71] shrink-0 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 sm:px-5 py-2.5 text-[10px] sm:text-xs font-bold uppercase tracking-widest transition-colors whitespace-nowrap border-r border-[#658d71] last:border-0 ${
                  activeTab === tab.id
                    ? 'bg-[#386646] text-[#d8ceb3]'
                    : tab.id === 'videos'
                    ? 'text-[#c4333e] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'
                    : 'text-[#a9c9b1] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'
                }`}
              >
                <SafeIcon icon={tab.icon} size={13} />
                {tab.label}
                {tab.id === 'videos' && activeTab !== 'videos' && (
                  <span className="ml-0.5 w-1.5 h-1.5 rounded-full bg-[#c4333e] shrink-0" />
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            {loading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
                <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading recap...</span>
              </div>
            )}
            {error && <p className="text-center text-[#e6a8a8] text-sm py-8">{error}</p>}
            {!loading && !error && (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.18 }}
                  className="flex flex-col gap-5"
                >
                  {activeTab === 'summary' && (
                    <>
                      <RecapHero game={game} feed={feed} />
                      <RecapDecisions game={game} feed={feed} />
                    </>
                  )}
                  {activeTab === 'performers' && <RecapTopPerformers feed={feed} />}
                  {activeTab === 'timeline' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Scoring Plays Timeline
                      </p>
                      <RecapTimeline allPlays={allPlays} />
                    </div>
                  )}
                  {activeTab === 'innings' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Inning-by-Inning Breakdown
                      </p>
                      <RecapInningBreakdown innings={innings} awayAbbr={awayAbbr} homeAbbr={homeAbbr} />
                    </div>
                  )}
                  {activeTab === 'videos' && (
                    <div>
                      <p className="text-[10px] text-[#658d71] uppercase tracking-widest mb-3 font-bold">
                        Game Highlights & Videos
                      </p>
                      <RecapVideos content={content} />
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GameRecap;